import {FormattedMessage, useIntl} from "react-intl";
import {Button, Col, Divider, Form, Input, Row, Select, Switch, Typography, Upload} from "antd";
import React, {ReactNode, SyntheticEvent, useContext, useEffect, useState} from "react";
import {AccessMode, MedicalStaff, MedicalStaffCreateWithAccess, PortalAccessStatus} from "../../api-client";
import {getMedicalStaffApi} from "../../api/apiInstances";
import {useNavigate} from "react-router-dom";
import '../../assets/css/Dentobook.css';
import '../../assets/css/GeneralForm.css';
import {CameraOutlined, CaretDownOutlined, EnvironmentOutlined, UserOutlined} from "@ant-design/icons";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import ReactQuill from "react-quill";
import {AlertContext} from "../../context/AlertContext";
import {RcFile, UploadProps} from "antd/es/upload";
import {ekatteMessages} from "../../i18n/ekatte";
import {UploadFile} from "antd/es/upload/interface";
import {isMobile} from "react-device-detect";
import {allCountries} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {ExitPromptContext} from "../../context/ExitPromptContext";
import {checkFormDirty} from "../../i18n/utils";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";

const {Option} = Select;

export interface MedicalStaffFormProps {
    mode: number;
    medicalStaff: MedicalStaff;
    setMedicalStaff?: Function;
    openModal: Function;
    setEditMode: Function;
    children?: JSX.Element | JSX.Element[];
}

interface SpecializationCodeMapping {
    [key: string]: string;
}

const MedicalStaffForm = ({medicalStaff, setMedicalStaff, mode, openModal, setEditMode}: MedicalStaffFormProps) => {

    const navigate = useNavigate();

    const intl = useIntl();

    const {setFormDirty} = useContext(ExitPromptContext);

    const {setVisible, setError, setSuccessMessage} = useContext(AlertContext);

    const {principal, locale} = useContext(DentobookApplicationContext);

    const [imagePreview, setImagePreview] = useState<string>('');

    const [formStaff, setFormStaff] = useState({...medicalStaff});
    const [, setStaffCountry] = useState(medicalStaff.country);
    const [avatar, setAvatar] = useState<RcFile | undefined>(undefined);
    const [avatarMouseIn, setAvatarMouseIn] = useState<boolean>(false);
    const [needsPortalAccess, setNeedsPortalAccess] = useState(false);

    const isCreate = !medicalStaff?.id;
    const hasPendingInvite = medicalStaff?.portalAccessStatus === PortalAccessStatus.PendingInvite
        || medicalStaff?.portalAccessStatus === PortalAccessStatus.Invited;
    const emailRequired = (isCreate && needsPortalAccess)
        || (!isCreate && (!!medicalStaff?.hasPortalAccess || hasPendingInvite));

    // Doctor-only clinical identity fields (specialisation, specialisation code, UIN).
    // Driven by the staff member's role — shown only for treating doctors. CLINIC_ADMIN is
    // preserved to avoid regressing existing admin-staff behavior. Hidden for DENTAL_TECHNICIAN,
    // MEDICAL_ASSISTANT, RECEPTIONIST and any unset role.
    const showDoctorFields = formStaff.role === 'MEDICAL_DOCTOR' || formStaff.role === 'CLINIC_ADMIN';

    const [imagePreviewJsx, setImagePreviewJsx] = useState<ReactNode>();

    const medicalApi = getMedicalStaffApi();

    const [form] = Form.useForm();

    const richTextEditorToolbarOptions = [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        ['bold', 'italic', 'underline', 'strike']
    ];

    let fileValidationError = '';

    const specializationToCodeMapping: SpecializationCodeMapping = {
        'specialization1': '64',
        'specialization2': '61',
        'specialization3': '62',
        'specialization4': '68'
    };

    const codeToSpecializationMapping: SpecializationCodeMapping = {
        '64': 'specialization1',
        '61': 'specialization2',
        '62': 'specialization3',
        '68': 'specialization4'
    };

    const [specializationOptions, setSpecializationOptions] = useState([
        {label: <FormattedMessage id="specialization1"/>, value: 'specialization1'},
        {label: <FormattedMessage id="specialization2"/>, value: 'specialization2'},
        {label: <FormattedMessage id="specialization3"/>, value: 'specialization3'},
        {label: <FormattedMessage id="specialization4"/>, value: 'specialization4'}
    ]);

    const handleSpecializationChange = (value: string) => {
        const code = specializationToCodeMapping[value];
        if (code) {
            setFormStaff(prevFormStaff => ({
                ...prevFormStaff,
                specialisation: value,
                specialisationCode: code
            }));
        } else {
            setFormStaff(prevFormStaff => ({
                ...prevFormStaff,
                specialisation: value
            }));
        }
        form.setFieldsValue({specialisation_code: code});
    };

    const handleSpecialisationCodeChange = (code: string) => {
        const specialization = codeToSpecializationMapping[code];
        if (specialization) {
            setFormStaff(prevFormStaff => ({
                ...prevFormStaff,
                specialisationCode: code,
                specialisation: specialization
            }));
        } else {
            setFormStaff(prevFormStaff => ({
                ...prevFormStaff,
                specialisationCode: code
            }));
        }
        form.setFieldsValue({specialisation: specialization});
    };

    const [formInitialState, setFormInitialState] = useState<Object | undefined>(undefined);

    useEffect(() => {
        if (!formInitialState) {
            setFormInitialState(form.getFieldsValue(true));
        }
    }, [form]);

    useEffect(() => {
        if (imagePreview) {
            setImagePreviewJsx(
                <button
                    onMouseEnter={() => setAvatarMouseIn(true)}
                    onMouseLeave={() => setAvatarMouseIn(false)}
                    className="avatar-upload-photo-img plain-button"
                >
                    <img width={150}
                         src={imagePreview}
                         alt="avatar"/>
                </button>
            );
        } else if (!medicalStaff.avatarUrl) {
            setImagePreviewJsx(
                <button onMouseEnter={() => setAvatarMouseIn(true)}
                        onMouseLeave={() => setAvatarMouseIn(false)}
                        className="avatar-upload-photo-img plain-button">
                    <UserIcon size="large" className="mb-4"/>
                </button>
            );
        } else {
            setImagePreviewJsx(
                <button
                    onMouseEnter={() => setAvatarMouseIn(true)}
                    onMouseLeave={() => setAvatarMouseIn(false)}
                    className="avatar-upload-photo-img plain-button"
                >
                    <img width={150}
                         src={medicalStaff.avatarUrl}
                         alt="avatar"/>
                </button>
            )
        }
    }, [imagePreview, medicalStaff.avatarUrl]);

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();

        try {
            form.submit();

            await form.validateFields();

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            // Non-doctor roles never carry doctor-only fields — drop any stale values
            // (e.g. left over after switching from MEDICAL_DOCTOR) so they are not submitted.
            if (!showDoctorFields) {
                formStaff.specialisation = '';
                formStaff.specialisationCode = '';
                formStaff.uin = '';
            }

            if (!medicalStaff.id) {
                handleCreateMedicalStaff();
            } else {
                handleUpdateMedicalStaff();
            }
        } catch (err: any) {
            const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;

            if (blankFieldExists) {
                setError(intl.formatMessage({id: 'all_fields_required'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        }
    };

    const handleCreateMedicalStaff = async () => {
        try {
            const staffPayload: MedicalStaffCreateWithAccess = {
                firstName: formStaff.firstName,
                lastName: formStaff.lastName,
                email: formStaff.email || null,
                phone: formStaff.phone || null,
                specialisation: formStaff.specialisation || null,
                uin: formStaff.uin || null,
                organizationId: principal?.user?.organizationId || '',
                userId: formStaff.userId || null,
                role: formStaff.role || null,
                roleBg: formStaff.roleBg || null,
                specialisationCode: formStaff.specialisationCode || null,
                secondName: formStaff.secondName || null,
                town: formStaff.town || null,
                country: formStaff.country || null,
                addressOne: formStaff.addressOne || null,
                addressTwo: formStaff.addressTwo || null,
                notes: formStaff.notes || null,
                accessMode: needsPortalAccess ? AccessMode.Invite : AccessMode.NoLogin,
                inviteEmail: needsPortalAccess ? (formStaff.email || undefined) : undefined,
            };

            let res;
            try {
                res = await medicalApi.createMedicalStaff(staffPayload);
            } catch (error: any) {
                handleCreateMedicalStaffError(error);
                return;
            }

            if (!res.data?.staff?.id) {
                throw new Error("Failed to create medical staff");
            }

            const medicalId = res.data.staff.id!;

            if (avatar) {
                await medicalApi.uploadStaffAvatar(medicalId, avatar as File);
            }

            if (setMedicalStaff) {
                setMedicalStaff({...formStaff});
            }

            setError('');
            const successKey = needsPortalAccess ? 'invite_sent_access' : 'medical_staff_created';
            setSuccessMessage(intl.formatMessage({ id: successKey }));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            navigate('/medical/staff');
        } catch (error) {
            handleCreateMedicalStaffError(error);
        }
    };


    const handleUpdateMedicalStaff = async () => {
        try {
            if (medicalStaff.id != null) {
                await medicalApi.updateMedicalStaff(medicalStaff.id, formStaff);
            }

            if (avatar) {
                await medicalApi.uploadStaffAvatar(medicalStaff.id!, avatar);
            }

            if (setMedicalStaff) {
                setMedicalStaff({...formStaff});
            }

            setError('');
            setSuccessMessage(intl.formatMessage({ id: 'medical_staff_updated' }));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            setEditMode(false);
        } catch (error) {
            handleUpdateMedicalStaffError(error);
        }
    };

    const handleCreateMedicalStaffError = (error: any) => {
        logger.log('Create medical staff error:', error);
        logger.log('Error response:', error?.response);
        logger.log('Error response data:', error?.response?.data);
        logger.log('Form data being sent:', formStaff);

        // Check for Python backend error format (FastAPI returns "detail")
        const errorMessage = error.response?.data?.detail || error.response?.data?.additionalMessage;

        if (errorMessage) {
            const msg = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : JSON.stringify(errorMessage).toLowerCase();

            if (msg.indexOf('email') > -1) {
                setError(intl.formatMessage({id: 'duplicate_email'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            } else {
                // Show generic error message for other errors
                logger.log('FastAPI validation error:', errorMessage);
                setError(typeof errorMessage === 'string' ? errorMessage : `Validation error: ${JSON.stringify(errorMessage)}`);
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        } else {
            logger.log('error', error);
        }
    };

    const handleUpdateMedicalStaffError = (error: any) => {
        // Check for Python backend error format (FastAPI returns "detail")
        const errorMessage = error.response?.data?.detail || error.response?.data?.additionalMessage;

        if (errorMessage) {
            const message = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : JSON.stringify(errorMessage).toLowerCase();

            if (message.indexOf('email') > -1) {
                setError(intl.formatMessage({id: 'duplicate_email'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            } else {
                // Show generic error message for other errors
                setError(typeof errorMessage === 'string' ? errorMessage : `Validation error: ${JSON.stringify(errorMessage)}`);
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        } else {
            logger.log('error', error);
        }
    };

    const props: UploadProps = {
        beforeUpload: (file: UploadFile) => {
            const fileName = file.name.toLowerCase();
            const validFormat = fileName.endsWith('png') || fileName.endsWith('jpg') || fileName.endsWith('jpeg');
            const validSize = file.size && file.size <= 1000000;

            if (!validFormat) {
                fileValidationError = intl.formatMessage({id: 'invalid_image_format'});
            } else if (!validSize) {
                fileValidationError = intl.formatMessage({id: 'invalid_image_size'});
            } else {
                fileValidationError = '';
            }
        },
        name: 'file',
        customRequest: () => {
        },
        showUploadList: false,
        onChange(info) {
            if (!fileValidationError) {
                formStaff.avatar = info.file.originFileObj?.name;
                setAvatar(info.file.originFileObj);

                setImagePreview(URL.createObjectURL(info.file.originFileObj as Blob));
            } else {
                setError(fileValidationError);
                setTimeout(() => {
                    setVisible(false)
                    setError('');
                }, ALERT_DURATIONS.STANDARD);
            }
        },
    };

    const filterOption = (input: string, option: any) => {
        const searchText = input.toLowerCase();
        return option?.searchLabel.includes(searchText) || option?.value.includes(searchText);
    };

    const handleTownChange = (value: string) => {
        setFormStaff({...formStaff, town: value});
    };

    return (
        <Form form={form} scrollToFirstError={true} name="horizontal_login" layout="vertical"
              onFieldsChange={(changedFields, allFields) => setFormDirty(checkFormDirty(allFields, formInitialState))}>
            <h4><span className="text-blue mr-1"><UserOutlined/></span> {<FormattedMessage
                id="personal_information"/>}
            </h4>

            <Upload {...props}>
                <div className={avatarMouseIn ? "avatar-upload-photo-mask" : ""}>
                    <CameraOutlined className={avatarMouseIn ? "avatar-upload-icon" : "d-none"}/>
                    {imagePreviewJsx}
                </div>
            </Upload>
            <Row gutter={16} className="mt-4">
                <Col flex={1.5}>
                    <Form.Item
                        label={<FormattedMessage id="first_name"/>}
                        className={"dentobook-form-item dentobook-simple-input"}
                        name="FirstName"
                        initialValue={formStaff.firstName}
                        rules={[{
                            pattern: /^[a-zA-Zа-яА-Я\-\s]+$/,
                            message: <FormattedMessage id="letters_only_field"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formStaff.firstName}
                            onChange={e => formStaff.firstName = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.5}>
                    <Form.Item
                        label={<FormattedMessage id="surname"/>}
                        name="Surname"
                        className={"dentobook-form-item dentobook-simple-input"}
                        initialValue={formStaff.secondName}
                        rules={[{
                            pattern: /^[a-zA-Zа-яА-Я\-\s]+$/,
                            message: <FormattedMessage id="letters_only_field"/>
                        }, {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formStaff.secondName ?? undefined}
                            onChange={e => formStaff.secondName = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.5}>
                    <Form.Item
                        label={<FormattedMessage id="last_name"/>}
                        name="LastName"
                        initialValue={formStaff.lastName}
                        className={"dentobook-form-item dentobook-simple-input"}
                        rules={[{
                            pattern: /^[a-zA-Zа-яА-Я\-\s]+$/,
                            message: <FormattedMessage id="letters_only_field"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formStaff.lastName}
                            onChange={e => formStaff.lastName = e.target.value}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col md={8}>
                    <Form.Item name="role"
                               className={"dentobook-form-item dentobook-simple-input input-full-width"}
                               label={<FormattedMessage id="role"/>}
                               rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                               initialValue={formStaff.role}>
                        <Select
                            disabled={mode === 1}
                            size={"large"}
                            value={formStaff.role}
                            suffixIcon={<CaretDownOutlined/>}
                            onChange={e => {
                                formStaff.role = e;

                                if (['MEDICAL_DOCTOR', 'CLINIC_ADMIN'].includes(e)) {
                                    setSpecializationOptions([
                                        {label: <FormattedMessage id="specialization1"/>, value: 'specialization1'},
                                        {label: <FormattedMessage id="specialization2"/>, value: 'specialization2'},
                                        {label: <FormattedMessage id="specialization3"/>, value: 'specialization3'},
                                        {label: <FormattedMessage id="specialization4"/>, value: 'specialization4'}
                                    ]);
                                } else {
                                    setSpecializationOptions([
                                        {label: <FormattedMessage id="dentist"/>, value: 'dentist'}
                                    ]);
                                }
                            }}
                            allowClear
                        >
                            <Select.Option value="CLINIC_ADMIN"><FormattedMessage id="CLINIC_ADMIN"/></Select.Option>
                            <Select.Option value="MEDICAL_DOCTOR"><FormattedMessage
                                id="MEDICAL_DOCTOR"/></Select.Option>
                            <Select.Option value="DENTAL_TECHNICIAN"><FormattedMessage
                                id="DENTAL_TECHNICIAN"/></Select.Option>
                            <Select.Option value="MEDICAL_ASSISTANT"><FormattedMessage
                                id="MEDICAL_ASSISTANT"/></Select.Option>
                            <Select.Option value="RECEPTIONIST"><FormattedMessage id="RECEPTIONIST"/></Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                { (['CLINIC_ADMIN', 'MEDICAL_DOCTOR'].includes(principal?.user?.role ?? '') &&
                showDoctorFields)
                ? (
                    <>
                        <Col md={8}>
                            <Form.Item name="specialisation"
                                       className={"dentobook-form-item dentobook-simple-input input-full-width"}
                                       label={<FormattedMessage id="specialisation"/>}
                                       rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                       initialValue={
                                           formStaff.specialisation
                                               ? intl.formatMessage({id: formStaff.specialisation})
                                               : ''
                                       }
                            >
                                <Select
                                    key={formStaff.specialisationCode}
                                    size={"large"}
                                    value={formStaff.specialisation}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={handleSpecializationChange}
                                    allowClear
                                >
                                    {specializationOptions.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={8}>
                            <Form.Item name="specialisation_code"
                                       className={"dentobook-form-item dentobook-simple-input input-full-width"}
                                       label={<FormattedMessage id="specialisation_code"/>}
                                       rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                       initialValue={formStaff.specialisationCode}

                            >
                                <Select
                                    key={formStaff.specialisation}
                                    size={"large"}
                                    value={formStaff.specialisationCode}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={handleSpecialisationCodeChange}
                                    allowClear
                                >
                                    <Option value="61"><FormattedMessage id="61"/></Option>
                                    <Option value="62"><FormattedMessage id="62"/></Option>
                                    <Option value="64"><FormattedMessage id="64"/></Option>
                                    <Option value="68"><FormattedMessage id="68"/></Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </>
                    ) : null
                }
            </Row>
            {showDoctorFields ? <Row gutter={16} className="mt-4">
                <Col md={8}>
                    <Form.Item
                        label={<FormattedMessage id="uin"/>}
                        name="uin"
                        initialValue={formStaff.uin}
                        className={"dentobook-form-item dentobook-simple-input input-full-width"}
                        rules={[{pattern: /^\d+$/, message: <FormattedMessage id="digits_only_field"/>},
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formStaff.uin ?? undefined}
                            onChange={e => formStaff.uin = e.target.value}/>
                    </Form.Item>
                </Col>
            </Row> : null}
            {!medicalStaff.id && (
                <Row gutter={24} style={{marginBottom: 8}}>
                    <Col flex={1}>
                        <Form.Item
                            className="dentobook-form-item"
                            label={<FormattedMessage id="needs_portal_access"/>}
                            name="needs_portal_access"
                        >
                            <Switch checked={needsPortalAccess} onChange={setNeedsPortalAccess}/>
                            <Typography.Text type="secondary" style={{display: 'block', marginTop: 4, fontSize: 12}}>
                                <FormattedMessage id="portal_access_invite_hint"/>
                            </Typography.Text>
                        </Form.Item>
                    </Col>
                </Row>
            )}
            <Divider/>
            <h4><span className="text-blue mr-1"><EnvironmentOutlined/></span> <FormattedMessage id="contact"/></h4>
            <Row gutter={24}>
                <Col flex={1.25}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email"/>}
                        name="email"
                        rules={[
                            {
                                required: emailRequired,
                                message: <FormattedMessage id="email_required_for_portal_access"/>
                            },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                                        ? Promise.resolve()
                                        : Promise.reject(<FormattedMessage id="email_invalid_format"/>);
                                }
                            }
                        ]}
                        initialValue={formStaff.email}
                    >
                        <Input
                            value={formStaff.email ?? undefined}
                            onChange={e => formStaff.email = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.25}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="phone_label_two"/>}
                        name="phone"
                        rules={[{
                            required: true,
                            pattern: new RegExp(/^[+()\d\s]+$/gm),
                            message: <FormattedMessage id="invalid_phone_number"/>
                        }]}
                        initialValue={formStaff.phone}
                    >
                        <Input type="tel"
                               value={formStaff.phone ?? undefined}
                               onChange={e => formStaff.phone = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.25} id="town-field">
                    <Form.Item
                        className={"dentobook-form-item input-full-width single-select"}
                        label={<FormattedMessage id="town"/>}
                        name="town"
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                        initialValue={formStaff.town}
                    >
                        <Select
                            className="dentobook-input"
                            showSearch
                            suffixIcon={<CaretDownOutlined/>}
                            placeholder="Търсене и избор"
                            optionFilterProp="label"
                            filterOption={filterOption}
                            onChange={handleTownChange}
                            options={ekatteMessages}
                        />
                    </Form.Item>
                </Col>
                <Col flex={1.25}>
                    <Form.Item
                        className={"dentobook-form-item input-full-width single-select"}
                        label={<FormattedMessage id="country"/>}
                        name="country"
                        initialValue={formStaff.country}
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            showSearch
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            autoComplete="new-state"
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-input"
                            value={formStaff.country}
                            onChange={e => {
                                formStaff.country = e;
                                setStaffCountry(e);
                            }}
                            filterOption={(input, option) => {
                                const searchText = input.toLowerCase();
                                const countryName = locale === 'BG'
                                    ? allCountries.find(c => c.code === option?.value)?.name_bg || ''
                                    : allCountries.find(c => c.code === option?.value)?.name_en || '';
                                return countryName.toLowerCase().includes(searchText);
                            }}
                        >
                            {allCountries.map(countryItem => (
                                <Select.Option key={countryItem.code}
                                               value={countryItem.code}>{locale === 'BG' ? countryItem.name_bg : countryItem.name_en}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row>
                <Form.Item
                    className={"dentobook-form-item dentobook-simple-input input-fill-width"}
                    label={<FormattedMessage id="addressOne"/>}
                    name="addressOne"
                    initialValue={formStaff.addressOne}
                >
                    <Input value={formStaff.addressOne ?? undefined}
                           onChange={e => formStaff.addressOne = e.target.value}/>
                </Form.Item>
            </Row>

            <Form.Item
                label={<FormattedMessage id="notes"/>}
                name="notes"
                initialValue={formStaff.notes}
                className="dentobook-form-item dentobook-simple-input mt-4"
                style={{marginBottom: isMobile ? 200 : 80}}
            >
                <ReactQuill
                    value={formStaff.notes ?? undefined}
                    onChange={e => formStaff.notes = e}
                    modules={{
                        toolbar: richTextEditorToolbarOptions
                    }}/>
            </Form.Item>
            <FormButtons>
                <Button
                    className={"dentobook-btn-secondary modal-btn"}
                    htmlType="button" onClick={() => openModal()}
                    type="default">
                    {<FormattedMessage id="cancel"/>}
                </Button>
                <Button
                    className={"dentobook-btn-primary modal-btn"}
                    onClick={handleSubmit} htmlType="submit"
                    type="primary">
                    {<FormattedMessage id="save"/>}
                </Button>
            </FormButtons>
        </Form>
    );
}

export default MedicalStaffForm;